// Side-effect import: <std-dsh-turn-row> must be registered before this
// hydrates one, and the class is otherwise referenced only as a type and for
// its statics.
import "./row";

import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css } from "@tento-ui/component-styles";
import { GESTURE_MANAGER } from "@tento-ui/gesture-manager";
import { Typography } from "@tento-ui/ui/typography";
import type { DshTurnRow } from "./row";

/** `ask`, in the dsh register — a disclosure ROW, not a card.
 *
 * This is the sharpest divergence between the two skins, and it is about
 * where interaction lives rather than about taste. The native skin makes an
 * ask the one card in the family because there the transcript IS the surface
 * a human answers on. dsh answers in the composer seat below the stream, so
 * the transcript's job is to say that something is being asked and what it is
 * about — one 24px line, in the same column as every other row — and to hold
 * the option list behind a disclosure for a reader scrolling back through
 * decided ones.
 *
 * Which is why the collapsed face must not scream. A pending ask takes the
 * row's `warn` state, and that state spends itself on the 16px leading box
 * and nowhere else: the reader needs to FIND the row, not to be stopped by
 * it, because the thing that stops them is the seat.
 *
 * A decided ask keeps the same row and gains a SUFFIX — the option that was
 * taken, dim. It does not disappear and it does not change shape: history
 * reading as history is the property, and a row that becomes something else
 * when it settles is a row a reader has to re-learn.
 *
 * `keepSubjectWhenOpen`, because the body does not restate the question. The
 * row's default is to drop the subject once the body is showing, which is
 * right when the body opens with the same words; here it opens with the
 * command and the options, so the question has to stay.
 *
 * `onOption` is still wired, and only while pending, because a caller with no
 * seat — a storybook, a review card, an embedded transcript — is a real
 * caller. Without it the option lines are read-only text, which is the right
 * thing in a transcript that cannot answer.
 *
 * The body is built as elements with INLINE styles: it is handed to the row,
 * which hands it to the accordion, so it lands two shadow roots away from
 * this file's stylesheet. Typography's sheet is in every shadow root, so the
 * ramp classes still resolve and only the values travel. */
@component({ name: "std-dsh-turn-ask" })
export class DshTurnAsk extends LonaWebComponent {
  private $row: DshTurnRow = this.hydrate("row");

  static makeWith(props: DshTurnAsk.Props): DshTurnAsk {
    return (DshTurnAsk.make() as DshTurnAsk).bind(props);
  }

  /** Every per-bind fact is recomputed from `props` and the row rebuilds its
   * whole tree, so a pooled instance arriving from a pending ask renders a
   * decided one with nothing left over. */
  bind(props: DshTurnAsk.Props): this {
    const pending = props.state === "pending";
    this.setAttribute("state", props.state);
    const decided = props.options.find(
      (option) => option.optionId === props.decidedOptionId,
    ) ?? null;

    this.$row.bind({
      kind: "ask",
      label: DshTurnAsk.LABEL,
      subject: props.title,
      suffix: decided?.name ?? null,
      state: pending ? "warn" : "idle",
      body: DshTurnAsk.buildBody(props, pending, decided),
      keepSubjectWhenOpen: true,
    });
    return this;
  }

  /** What was asked and what could be answered. The command comes first
   * because it is the thing being decided; the options follow as lines, one
   * per option, with the taken one marked.
   *
   * The body's own margin and indent are NOT set here — the row writes them
   * onto this element, because an element handed to the accordion as content
   * becomes the padded box itself. */
  private static buildBody(
    props: DshTurnAsk.Props,
    pending: boolean,
    decided: Option<DshTurnAsk.AskOption>,
  ): HTMLElement {
    const $body = document.createElement("div");
    Object.assign($body.style, {
      display: "flex",
      flexDirection: "column",
      gap: "2px",
    });
    const command = props.command ?? "";
    if (command !== "") $body.appendChild(DshTurnAsk.buildCommand(command));
    for (const option of props.options) {
      $body.appendChild(
        DshTurnAsk.buildOption(
          option,
          option.optionId === decided?.optionId,
          pending ? props.onOption ?? null : null,
        ),
      );
    }
    return $body;
  }

  /** Verbatim and monospace, for the reason a permission prompt is read at
   * all: a command's flags and operators are columns a reader scans, not
   * prose. It is NOT syntax-coloured here — the native skin's card colours it
   * because the decision is made on that card, and this row's reader has
   * already been handed the decision in the seat below. */
  private static buildCommand(command: string): HTMLElement {
    const $command = document.createElement("pre");
    $command.className = "code-block";
    $command.textContent = command;
    Object.assign($command.style, {
      margin: "0 0 6px",
      padding: "8px 10px",
      borderRadius: "8px",
      border: "1px solid var(--divider, rgba(127, 127, 127, 0.25))",
      background: "var(--bg-hover, rgba(127, 127, 127, 0.08))",
      color: "var(--text-sec, #6a7186)",
      fontFamily:
        "var(--font-mono, ui-monospace, SFMono-Regular, Menlo, monospace)",
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
      maxHeight: "128px",
      overflow: "hidden auto",
    });
    // The knobs the .code-block rule reads, as custom properties rather than
    // a literal font-size: the ramp divides --font-size by the face's cap
    // height, so a literal wins the size and loses the metrics.
    $command.style.setProperty(
      "--font-size",
      "var(--turn-fine-size, var(--text-8px))",
    );
    $command.style.setProperty("--line-height-ratio", "1.5");
    return $command;
  }

  /** One option, as a line. The mark is a check for the decided one and a
   * hairline dash for the rest — the smallest pair of glyphs that survives at
   * this size, where a filled and an empty circle are one grey smudge. */
  private static buildOption(
    option: DshTurnAsk.AskOption,
    isDecided: boolean,
    onOption: Option<(optionId: string) => void>,
  ): HTMLElement {
    const $line = document.createElement("div");
    Object.assign($line.style, {
      display: "flex",
      alignItems: "center",
      minHeight: "20px",
      minWidth: "0",
      borderRadius: "4px",
      cursor: onOption == null ? "default" : "pointer",
    });
    const $mark = Typography.small(isDecided ? "✓" : "–");
    Object.assign($mark.style, {
      flex: "none",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: "14px",
      marginTop: "0",
      marginBottom: "0",
      marginRight: "6px",
      color: isDecided
        ? "var(--fg-green, #2da44e)"
        : "var(--text-ter, #8b93a7)",
      opacity: isDecided ? "1" : "0.7",
    });
    const $name = Typography.small(option.name);
    Object.assign($name.style, {
      flex: "1",
      minWidth: "0",
      marginTop: "0",
      marginBottom: "0",
      color: isDecided
        ? "var(--text-sec, #6a7186)"
        : "var(--text-ter, #8b93a7)",
      opacity: isDecided ? "1" : "0.8",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    });
    $line.append($mark, $name);
    if (onOption != null) {
      // pointerdown/up through the house manager, never onclick — the manager
      // owns the app's gesture lock, so a line bound with a click handler
      // fires under a drag the rest of the app has already claimed.
      GESTURE_MANAGER.addPointerEvent($line, {
        onClick: () => onOption(option.optionId),
      });
    }
    return $line;
  }

  static $styles = [
    css`
      :host {
        display: block;
        width: 100%;
        min-width: 0;
      }
    `,
  ];

  static $html: Option<HTMLTemplateElement> = html`
    <std-dsh-turn-row id="row"></std-dsh-turn-row>
  `;
}

export namespace DshTurnAsk {
  export type State = "pending" | "decided";

  /** Named `AskOption` rather than `Option` so it cannot shadow the global
   * `Option<T>` inside this namespace. Structurally the contract's own type. */
  export type AskOption = {
    optionId: string;
    name: string;
    /** Protocol option kind — "allow_once", "allow_always", "reject_once", …
     * Open text: a skin may style by prefix but must not switch on an
     * exhaustive list it does not own. */
    kind: string;
  };

  /** The row's leading glyph comes from `DshTurnRow.GLYPH.ask`, not from
   * here — a component that pasted its own character would be the second
   * place the skin's gutter is decided. Only the WORD is this file's. */
  export const LABEL = "Ask";

  export type Props = {
    title: string;
    command?: string;
    options: AskOption[];
    state: State;
    /** Which option was taken, once `state` is "decided". Optional: a caller
     * that did not keep the answer still gets the row, with the question on
     * it and no suffix. */
    decidedOptionId?: string;
    onOption?: (optionId: string) => void;
  };
}
